import type { BIRevision } from '@/shared/generated'
import type { WidgetPeriods } from '../widgets/types'

import { useMemo } from 'react'
import { Navigate } from '@tanstack/react-router'
import { prop } from 'lodash/fp'
import { ChartPieIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useBIRevisionQuery } from '@/entities/bi'
import { Access } from '@/entities/session'

import DocumentTitle from '@/shared/components/DocumentTitle'
import CmpLogoIcon from '@/shared/components/icons/CmpLogoIcon'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
} from '@/shared/components/ui/breadcrumb'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Content, Header } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'

import { getMoMPeriods } from '../../lib/helpers'
import AiDashboardCommonSection from './AiDashboardCommonSection'

export default function AiDashboard() {
	const { t } = useTranslation()

	const biRevisionDateQuery = useBIRevisionQuery<string | null>({
		refetchOnMount: 'always',
		select: prop('date') as (data: BIRevision | null) => string | null,
	})
	const periods = useMemo(() => {
		if (!biRevisionDateQuery.data) return null
		const defaultPeriod = getMoMPeriods(biRevisionDateQuery.data)?.main

		return { main: defaultPeriod, compared: defaultPeriod }
	}, [biRevisionDateQuery.data])

	return (
		<Access permissions={['ACCESS_BI']} fallback={<Navigate to='/p/og' />}>
			<DocumentTitle title={t('ai_dashboard_title')}>
				<Header className='h-11 items-center border-b px-4 py-2'>
					<ChartPieIcon className='size-4' />
					<Separator orientation='vertical' className='mx-2 h-5' />
					<Breadcrumb className='text-sm'>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbPage>{t('ai_dashboard_title')}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				</Header>
				<Content className='overflow-hidden'>
					<div className='size-full overflow-hidden'>
						<div
							className='relative h-full overflow-y-scroll'
							id='dashboard-scrollable-area'
						>
							{biRevisionDateQuery.isFetching ? (
								<Empty className='h-full'>
									<EmptyHeader>
										<EmptyMedia variant='icon'>
											<Spinner />
										</EmptyMedia>
										<EmptyTitle>
											{t('general_loading', { defaultValue: 'Loading...' })}
										</EmptyTitle>
										<EmptyDescription>
											{t('general_loading_description', {
												defaultValue: 'Please wait while we load the data...',
											})}
										</EmptyDescription>
									</EmptyHeader>
								</Empty>
							) : periods ? (
								<AiDashboardCommonSection
									commonPeriods={periods as WidgetPeriods}
								/>
							) : (
								<Empty data-testid='no-data' className='h-full'>
									<EmptyHeader>
										<EmptyMedia variant='icon'>
											<CmpLogoIcon className='fill-primary' />
										</EmptyMedia>
										<EmptyTitle>
											{t('dashboard.unavailable.title', {
												defaultValue:
													'This page is currently switched off for your domain',
											})}
										</EmptyTitle>
										<EmptyDescription>
											{t('dashboard.unavailable.subtitle', {
												defaultValue:
													'Please contact our support to switch it on for you and your team.',
											})}
										</EmptyDescription>
									</EmptyHeader>
								</Empty>
							)}
						</div>
					</div>
				</Content>
			</DocumentTitle>
		</Access>
	)
}
